import React, { useEffect, useRef, useState } from "react";
import dynamic from "next/dynamic";

import getStrainQuiz from "api/requests/userProfileService/getStrainQuiz";
import { StrainQuiz } from "custom-types/StrainQuiz";
import useDomainCountryCode from "hooks/useDomainCountryCode";
import { useEventTracker } from "hooks/useEventTracker";
import { useSelector } from "redux/hooks";
import useUserStrainPreferences from "redux/hooks/useUserStrainPreferences";
import { getUserStrainPreferences } from "redux/selectors/user";

import CollapsibleChips from "components/botanic/CollapsibleChips";
import StrainPrizeIcon from "components/Icons/strain-prize-mono.svg";
import TrackImpression from "components/TrackImpression";

const StrainQuizModal = dynamic(
  () => import("components/StrainQuiz/StrainQuizModal"),
);

const ProfileStrainQuizPreferences = () => {
  const { publishEvent } = useEventTracker();

  const [quizIsVisible, setQuizIsVisible] = useState(false);
  const [quiz, setQuiz] = useState<StrainQuiz>();
  const userPreferences = useSelector(getUserStrainPreferences);
  useUserStrainPreferences();

  const [displayedQuizPreferences, setDisplayedQuizPreferences] = useState([]);
  const countryCode = useDomainCountryCode();
  const editPreferencesButtonRef = useRef<HTMLButtonElement>(null);

  // @ts-ignore (fix me please, do not replicate)
  const logEvent = async (bool) => {
    if (bool) {
      publishEvent({
        action: "click",
        category: "Strain Quiz Account",
        label: "expand best buds preferences",
      });
    }
    return;
  };

  const onQuizClose = async () => {
    setQuizIsVisible(false);
  };

  const handleStrainQuizCtaClick = () => {
    publishEvent({
      action: "click",
      category: "Strain Quiz Account",
      label: "edit best buds preferences",
    });
    setQuizIsVisible(true);
  };

  useEffect(() => {
    const getQuiz = async () => {
      const data = await getStrainQuiz(countryCode);
      if (data) {
        setQuiz(data);
      }
    };
    if (!quiz) {
      getQuiz();
    }
  }, []);

  useEffect(() => {
    if (userPreferences?.preferences?.length && quiz) {
      const gatheredPreferences = userPreferences.preferences.reduce(
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- TODO: fix me please, do not replicate
        (acc: any, pref: any) => {
          const { type_slug, options } = pref;

          const quizReference = quiz.filter(
            (question) => question.slug === type_slug,
          )[0];
          if (type_slug === "avoid") {
            // @ts-ignore (fix me please, do not replicate)
            const negatives = options.map((o) => {
              if (o === "dry_eyes" || o === "dry_mouth") {
                return `avoid ${o.replace("_", " ")}`;
              }
              return `avoid feeling ${o}`;
            });
            acc = acc.concat(negatives);
          } else if (
            (type_slug === "energize_score" || type_slug === "thc") &&
            quizReference
          ) {
            // @ts-ignore (fix me please, do not replicate)
            options.forEach((o) => {
              const quizOption = quizReference.options
                .filter((op) => op.slug === o)
                .map((ele) => ele.displayName);
              acc = acc.concat(quizOption);
            });
          } else {
            acc = acc.concat(options);
          }
          return acc;
        },
        [],
      );
      setDisplayedQuizPreferences(gatheredPreferences);
    }
  }, [userPreferences, quiz]);

  return (
    <>
      {displayedQuizPreferences.length > 0 && quiz && (
        <TrackImpression
          trackingFunction={() =>
            publishEvent({
              action: "impression",
              category: "Strain Quiz Account",
              label: "my best buds",
            })
          }
        >
          <section className="shadow-mid mb-xl rounded">
            <div className="p-lg">
              <div className="flex mb-sm items-center">
                <StrainPrizeIcon
                  className="mr-xs text-default"
                  height="25"
                  width="25"
                />
                <h3>My best bud preferences</h3>
              </div>
              <div data-testid="profile-strain-preferences">
                <CollapsibleChips
                  logEvent={logEvent}
                  className="border m-xs border-light-grey text-xs bg-white rounded inline-block px-md"
                  chips={displayedQuizPreferences}
                  chipButtonClassName="text-xs text-green inline-block ml-xs"
                />
              </div>
            </div>

            <div className="border-light-grey border-t text-center">
              <button
                onClick={handleStrainQuizCtaClick}
                className="font-bold text-xs underline p-md"
                ref={editPreferencesButtonRef}
              >
                edit preferences
              </button>
            </div>
          </section>
          {quizIsVisible ? (
            <StrainQuizModal
              existingPreferences={
                // pass a deep copy so it will be safe to mutate in the modal:
                JSON.parse(JSON.stringify(userPreferences?.preferences || []))
              }
              onQuizClose={onQuizClose}
              returnFocusRef={editPreferencesButtonRef}
            />
          ) : (
            <></>
          )}
        </TrackImpression>
      )}
    </>
  );
};

export default ProfileStrainQuizPreferences;
